import Button from '@cobliteam/colib-ui/Button';
import Checkbox from '@cobliteam/colib-ui/Checkbox';
import { DialogBody, DialogContent, DialogDescription, DialogFooter, DialogTitle } from '@cobliteam/colib-ui/Dialog';
import Icon from '@cobliteam/colib-ui/Icon';
import Label from '@cobliteam/colib-ui/Label';
import Typography from '@cobliteam/colib-ui/Typography';
import type { Preferences } from '@cobliteam/common-types';
import { alertHookConfig } from '@cobliteam/hooks/src/useAlertHooks/alertHookConfig';
import type { CurrentAlertHook } from '@cobliteam/hooks/src/useAlertHooks/types';
import useAlertHooksStore from '@cobliteam/hooks/src/useAlertHooks/useAlertHooksStore';
import useLocalStorage from '@cobliteam/hooks/src/useLocalStorage';
import { useTranslation } from '@cobliteam/i18n';
import { useState } from 'react';
import { trackDismissed, trackExperienceViewed } from './analytics';

interface Benefit {
  icon: string;
  label: string;
  description?: string;
}
interface DialogAlertHookSuggestionProps {
  current: CurrentAlertHook;
}

/**
 * Step 1 — pitches the alert. "Ver sugestão de alerta" advances to the preview step
 * (DialogAlertHookConfirm), where the trigger is actually created.
 *
 * The checkbox below the benefits is the **global** opt-out: ticking it and clicking "Ignorar"
 * persists `hideAlertHookSuggestions=true` on the local preferences, which short-circuits every
 * future call to `useTriggerAlertHook`.
 */
const DialogAlertHookSuggestion = ({ current }: DialogAlertHookSuggestionProps) => {
  const { t } = useTranslation(['alertHooks']);
  const advanceToConfirm = useAlertHooksStore((state) => state.advanceToConfirm);
  const dismiss = useAlertHooksStore((state) => state.dismiss);
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const config = alertHookConfig[current.type];
  const { copy } = config;
  const dismissKey = config.getDismissKey(current.params);
  const alertType =
    config.kind === 'blueprint'
      ? {
          alert_type: config.alertType,
        }
      : {};
  const benefits = t(copy.suggestion.benefitsKey, {
    returnObjects: true,
    defaultValue: [],
  }) as Benefit[];
  const handleSeeExperience = () => {
    trackExperienceViewed({
      hookType: current.type,
      dismissKey,
      ...alertType,
    });
    advanceToConfirm();
  };
  const handleDismiss = () => {
    trackDismissed({
      hookType: current.type,
      dismissKey,
      dontShowAgain,
      ...alertType,
    });
    if (dontShowAgain) {
      setPreferences({
        ...(preferences ?? ({} as Preferences)),
        hideAlertHookSuggestions: true,
      });
    }
    dismiss();
  };
  return (
    <DialogContent size='sm' className='flex flex-col gap-0 p-0' showCloseButton={false}>
      <DialogBody className='px-6 pt-2 pb-4'>
        <div className='mb-4 flex flex-col items-center justify-center pt-8 text-center'>
          <div className={`flex h-14 w-14 items-center justify-center rounded-full ${copy.suggestion.iconBg}`}>
            <Icon name={copy.suggestion.iconName as never} size='lg' className='text-link' />
          </div>
          <DialogTitle className='mt-4 text-center'>{t(copy.suggestion.titleKey)}</DialogTitle>
          <DialogDescription
            render={
              <Typography variant='caption' className='mt-2 gap-1 break-words px-6 text-center text-muted-foreground'>
                {t(copy.suggestion.descriptionKey)}
              </Typography>
            }
          />
        </div>

        <div className='rounded-2xl border border-border bg-background p-5'>
          <Typography variant='label' className='mb-3 block gap-1 break-words text-foreground'>
            {t('alertHooks:common.benefitsTitle')}
          </Typography>
          <ul className='flex flex-col gap-3'>
            {benefits.map((benefit) => (
              <li key={benefit.label} className='flex items-start gap-3'>
                <Icon name={benefit.icon as never} size='sm' className='mt-0.5 shrink-0 text-link' />
                <div className='flex flex-col gap-0.5'>
                  <Typography variant='label' className='gap-1 break-words text-foreground'>
                    {benefit.label}
                  </Typography>
                  {benefit.description && (
                    <Typography variant='caption' className='gap-1 break-words text-muted-foreground'>
                      {benefit.description}
                    </Typography>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className='mt-5 flex items-center justify-center gap-2'>
          <Checkbox id='dialog-alert-hook-dont-show-again' checked={dontShowAgain} onCheckedChange={(value) => setDontShowAgain(value === true)} />
          <Label htmlFor='dialog-alert-hook-dont-show-again' className='cursor-pointer'>
            <Typography variant='caption' className='gap-1 break-words text-muted-foreground'>
              {t('alertHooks:common.dontShowAgain')}
            </Typography>
          </Label>
        </div>
      </DialogBody>

      <DialogFooter className='flex flex-col gap-2 px-6 pt-4'>
        <Button onClick={handleSeeExperience} className='w-full'>
          <Icon size='sm' name='eye' />
          {t('alertHooks:common.ctaSee')}
        </Button>
        <Button variant='ghost' onClick={handleDismiss} className='w-full'>
          {t('alertHooks:common.ctaDismiss')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
export default DialogAlertHookSuggestion;
